import type {
  DoctorIntakeFormView,
  PatientVisibleNoteDto,
  QuotePricingCard,
  QuotePricingCardKey,
  TravelPlanningLoadResponse,
} from '@jema-manager/shared';
import { apiClient } from './api-client';

export type DoctorPatientProgressKey =
  | 'travel_date_confirmed'
  | 'trip_planned'
  | 'deposit_paid'
  | 'reached'
  | 'complete';

export interface DoctorPatientProgress {
  currentStep: DoctorPatientProgressKey | null;
  travelDateConfirmed: boolean;
  tripPlanned: boolean;
  depositPaid: boolean;
  reached: boolean;
  complete: boolean;
}

export interface DoctorPatientQuoteSummary {
  quoteId: string;
  serviceRequestId: string;
  travelCaseId: string | null;
  referenceUid: string | null;
  specialty: string | null;
  currentStage: string | null;
  acceptedAt: string;
  unread: boolean;
  procedureAmount: number;
  currency: string;
  selectedPlanKey: QuotePricingCardKey | null;
  selectedPlanLabel: string | null;
  pricingCards: QuotePricingCard[];
  recommendedPlanKey: QuotePricingCardKey | null;
  treatmentTypes: string[];
  procedureTags: string[];
  procedures: Array<{
    lineItemId: string;
    name: string;
    amount: number;
  }>;
  jemaNotes?: PatientVisibleNoteDto[];
}

export interface DoctorPatientListItem {
  patientId: string;
  patientName: string;
  referenceUid: string | null;
  referenceUids: string[];
  unread: boolean;
  acceptedQuotesCount: number;
  primaryQuoteId: string;
  primaryServiceRequestId: string;
  procedureTags: string[];
  procedureAmount: number;
  currency: string;
  selectedPlanLabels: string[];
  progress: DoctorPatientProgress;
  acceptedQuotes: DoctorPatientQuoteSummary[];
}

export interface DoctorPatientDetail extends DoctorPatientListItem {
  acceptedQuotes: Array<DoctorPatientQuoteSummary & { intake: DoctorIntakeFormView | null }>;
  travelPlans: TravelPlanningLoadResponse[];
  jemaNotes: PatientVisibleNoteDto[];
}

export interface GetDoctorPatientsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface DoctorPatientsListResponse {
  data: DoctorPatientListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    hasMore: boolean;
  };
}

export interface DoctorPatientsUnreadCountResponse {
  unreadCount: number;
}

export const doctorPatientsApi = {
  list: async (params?: GetDoctorPatientsParams): Promise<DoctorPatientsListResponse> => {
    return apiClient.get<DoctorPatientsListResponse>('/doctors/patients', params);
  },

  detail: async (patientId: string): Promise<{ data: DoctorPatientDetail }> => {
    return apiClient.get<{ data: DoctorPatientDetail }>(
      `/doctors/patients/${encodeURIComponent(patientId)}`
    );
  },

  unreadCount: async (): Promise<{ data: DoctorPatientsUnreadCountResponse }> => {
    return apiClient.get<{ data: DoctorPatientsUnreadCountResponse }>(
      '/doctors/patients/unread-count'
    );
  },
};
